import { useState, FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportError } from '../services/api';

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justVerified = searchParams.get('verified') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      // Let RootRedirect handle group-based routing — avoids reading stale context state
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Login failed. Please try again.';

      let friendly = raw;
      if (raw.includes('User does not exist') || raw.includes('Incorrect username') || raw.includes('Incorrect password')) {
        friendly = 'Email or password is incorrect.';
      } else if (raw.includes('not confirmed')) {
        navigate(`/verify?email=${encodeURIComponent(email)}`);
        return;
      } else if (raw.includes('Password attempts exceeded')) {
        friendly = 'Too many failed attempts. Please wait a few minutes and try again.';
      } else if (raw.includes('disabled')) {
        friendly = 'This account has been disabled. Contact support.';
      }

      setError(friendly);

      // Report non-trivial auth errors to admin (wrong password spam is medium, disabled accounts are high)
      const severity = raw.includes('disabled') ? 'high' : raw.includes('attempts exceeded') ? 'medium' : 'low';
      reportError({
        severity,
        category: 'auth',
        message: `Login failed: ${friendly}`,
        details: { rawError: raw },
        userEmail: email.trim().toLowerCase(),
        url: window.location.href,
      }).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <Link to="/" style={s.homeLink}>← Home</Link>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoMark}>🎓</div>
          <div>
            <div style={s.logoTitle}>Academic Allies</div>
            <div style={s.logoSub}>AI Learning Center</div>
          </div>
        </div>

        <h1 style={s.heading}>Welcome back</h1>
        <p style={s.sub}>Sign in to your account</p>

        {justVerified && (
          <div style={s.successBox}>
            ✓ Email verified! You can now sign in.
          </div>
        )}

        {error && (
          <div style={s.errorBox} role="alert">
            {error}
            {error.includes('already') && (
              <button
                style={s.retryLink}
                onClick={() => { setError(''); handleSubmit({ preventDefault: () => {} } as FormEvent); }}
              >
                Try again →
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={s.form}>
          <label style={s.label}>Email address</label>
          <input
            type="email" required autoFocus
            value={email} onChange={e => setEmail(e.target.value)}
            style={s.input} placeholder="you@example.com"
          />

          <label style={s.label}>Password</label>
          <input
            type="password" required
            value={password} onChange={e => setPassword(e.target.value)}
            style={s.input} placeholder="••••••••"
          />

          <button type="submit" disabled={loading} style={s.btn}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <Link to="/forgot-password" style={{ ...s.link, fontSize: '13px', fontWeight: 500 }}>
            Forgot password?
          </Link>
        </div>

        <div style={s.footer}>
          Don't have an account? <Link to="/signup" style={s.link}>Sign up</Link>
        </div>
      </div>

      <style>{`
        input:focus { border-color: #4F46E5 !important; outline: none; box-shadow: 0 0 0 3px rgba(79,70,229,0.15) !important; }
        button:not(:disabled):hover { opacity: 0.9; }
      `}</style>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh', background: '#F4F3FF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px', position: 'relative',
  },
  homeLink: {
    position: 'absolute', top: '20px', left: '24px',
    fontSize: '13px', fontWeight: 600, color: '#64748B',
    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px',
  },
  card: {
    background: '#fff', borderRadius: '16px',
    boxShadow: '0 4px 32px rgba(79,70,229,0.1)',
    padding: '40px', width: '100%', maxWidth: '420px',
  },
  logo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' },
  logoMark: {
    width: '40px', height: '40px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px',
  },
  logoTitle: { fontSize: '15px', fontWeight: 800, color: '#1E1B4B', letterSpacing: '-0.01em' },
  logoSub: { fontSize: '10px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' },
  heading: { fontSize: '24px', fontWeight: 800, color: '#1E1B4B', letterSpacing: '-0.02em', marginBottom: '4px' },
  sub: { fontSize: '14px', color: '#64748B', marginBottom: '24px' },
  errorBox: {
    background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
    padding: '10px 14px', fontSize: '13px', color: '#991B1B', marginBottom: '16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
  },
  retryLink: { background: 'none', border: 'none', color: '#991B1B', fontWeight: 700, cursor: 'pointer', fontSize: '13px', padding: 0, flexShrink: 0 },
  successBox: { background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#166534', fontWeight: 600, marginBottom: '16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px', marginTop: '12px' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', color: '#111827',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  },
  btn: {
    marginTop: '20px',
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    color: '#fff', border: 'none', borderRadius: '8px',
    padding: '12px', fontSize: '14px', fontWeight: 700,
    cursor: 'pointer', transition: 'opacity 0.15s',
  },
  footer: { fontSize: '13px', color: '#64748B', textAlign: 'center', marginTop: '20px' },
  link: { color: '#4F46E5', fontWeight: 600, textDecoration: 'none' },
};
